import React, { useState, useEffect, useRef } from 'react';
import Localize from 'react-intl-universal';

import { get } from 'lodash-es';

import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import FormHelperText from '@mui/material/FormHelperText';
import TextField from '@mui/material/TextField';

import { getByPathAndParams } from '@services/BaseApi';

const AutocompleteComponent = ({
  field = {},
  form: { touched, errors, setFieldTouched } = {},
  path,
  pathVariables,
  pathVariablesRequired = undefined,
  formItemChangeHandler = () => {},
  labelStyle = {},
  responseFormater = (data) => data,
  getOptionLabel = (option) => option?.value || option,
  isOptionEqualToValue = (option, value) => option.id === value.id,
  label = '',
  required = false,
  hideItemId = null,
  disableClearable = false,
  helperText,
  temApi = false,
  ...rest
}) => {
  const nameRef = useRef();
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  const getData = async () => {
    try {
      if (!pathVariablesRequired) {
        const response = path && (await getByPathAndParams({ path, temApi }));
        if (response?.data) {
          setOptions(responseFormater(response.data));
        }
      } else {
        if (
          !pathVariablesRequired
            .map((requiredVariable) => {
              return pathVariables[requiredVariable] ? true : false;
            })
            .includes(false)
        ) {
          const response = path && (await getByPathAndParams({ path, pathVariables, temApi }));
          if (response?.data) {
            setOptions(responseFormater(response.data));
          }
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) {
      setOptions([]);
    } else {
      setLoading(true);
      getData();
    }
  }, [open]);

  return (
    <Autocomplete
      sx={{
        backgroundColor: (theme) => theme.palette.booking.backgroundField,
        '.MuiSvgIcon-root ': {
          fill: (theme) => theme.palette.primary.main,
          color: (theme) => theme.palette.primary.main
        }
      }}
      value={field?.value || null}
      ref={nameRef}
      data-test-id='autocomplete-component'
      size='small'
      disableClearable={disableClearable}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      onKeyPress={() => {
        setOpen(true);
      }}
      isOptionEqualToValue={isOptionEqualToValue}
      getOptionLabel={getOptionLabel}
      options={hideItemId !== null ? options?.filter((el) => el.id !== hideItemId) : options || []}
      loading={loading}
      aria-busy={loading}
      aria-describedby={`${field?.name}-loading`}
      noOptionsText={Localize.get('App.noOptions')}
      onChange={(event, newValue) => {
        const nameGetter = nameRef.current.getAttribute('name');
        const index = nameRef.current.getAttribute('data-index');
        const data = { name: nameGetter, newValue };
        formItemChangeHandler(event, data, field?.name, index);
      }}
      {...rest}
      renderInput={(params) => (
        <>
          <TextField
            {...labelStyle}
            {...params}
            id={field?.name}
            required={required}
            aria-required={required}
            label={label}
            onBlur={() => setFieldTouched(field?.name, true, true)}
            error={Boolean(get(touched, field?.name) && get(errors, field?.name))}
            helperText={get(touched, field?.name) && get(errors, field?.name)}
            aria-describedby={`${field?.name}-helper-text ${loading ? `${field?.name}-loading` : ''}`}
            FormHelperTextProps={{
              id: `${field?.name}-helper-text`,
              ['data-test-id']: field?.name
            }}
            InputProps={{
              ...params.InputProps,
              name: field?.name,
              autoComplete: 'new-password',
              endAdornment: (
                <>
                  {loading ? (
                    <CircularProgress id={`${field?.name}-loading`} color='inherit' size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              )
            }}
          />
          {helperText && (
            <FormHelperText id={`${field?.name}-helper-text`}>
              {Localize.get(helperText)}
            </FormHelperText>
          )}
        </>
      )}
    />
  );
};

export default AutocompleteComponent;
